import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatSnackBarService } from "app/core/services/snackbar-service/snackbar.service";
import { environment } from "environments/environment";
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { ErrorHandlerUtil } from "app/core/util/error-handler.util";

@Component({
    selector: "app-pdf-view",
    templateUrl: "./pdf-view.component.html",
    styleUrls: ["./pdf-view.component.scss"],
    standalone: true,
    imports: [
      CommonModule,
      MatButtonModule,
      MatIconModule,
      MatProgressSpinnerModule,
      MatTooltipModule,
      PdfViewerModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PdfViewComponent implements OnInit, OnChanges {
  @Input() src: string;
  @Input() height: string = '100%';
  @Input() showToolbar: boolean = false;

  baseUrl: string = environment.GOOGLE_DOCS_URL;
  pdfSrc: string;
  zoom = 1;
  rotation = 0;
  showAll = true;
  pageVariable = 1;
  downloading: boolean = false;

  constructor(
    private _snackBarService: MatSnackBarService,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Get URL from sessionStorage if available
    let pdfUrl = sessionStorage.getItem('viewerUrl');

    if (pdfUrl) {
      this.setPdfSrc(pdfUrl);
      sessionStorage.removeItem('viewerUrl'); // Clean up
      this._cdr.detectChanges();
    } else {
      // Fallback for when component is used with @Input() src
      if (this.src) {
        this.setPdfSrc(this.src);
      } else {
        this._snackBarService.error("PDF URL not found");
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['src'] && changes['src'].currentValue) {
      this.setPdfSrc(changes['src'].currentValue);
      this._cdr.detectChanges();
    }
  }

  setPdfSrc(url: string): void {
    console.log("=== setPdfSrc Debug ===");
    console.log("Input URL:", url);
    console.log("URL type:", typeof url);
    console.log("URL length:", url ? url.length : 0);
    
    if (!url) {
      console.error("No URL provided to setPdfSrc");
      this._snackBarService.error("PDF not found");
      return;
    }
    
    try {
      console.log("Setting PDF URL:", url);
      this.pdfSrc = url;
      console.log("PDF src set to:", this.pdfSrc);
      this._cdr.detectChanges();
      console.log("Change detection triggered");
    } catch (error) {
      console.error("Error setting PDF URL:", error);
      this._snackBarService.error("Failed to load PDF");
    }
    console.log("=== End setPdfSrc Debug ===");
  }

  onPageRendered(e: any) {
    console.log('Page rendered:', e);
  }

  onPdfLoadingFailed(error: any) {
    console.error('PDF loading failed:', error);
    this._snackBarService.error("Failed to load PDF");
  }

  async downloadPdf(): Promise<void> {
    if (!this.pdfSrc) {
      this._snackBarService.error("PDF not available for download");
      return;
    }

    this.downloading = true;
    this._cdr.detectChanges();

    try {
      const response = await fetch(this.pdfSrc);
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = this.getFileNameFromUrl(this.pdfSrc);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      const errorMsg = ErrorHandlerUtil.extractErrorMessage(error);
      console.error('Error downloading PDF:', error);
      this._snackBarService.error(`Failed to download PDF: ${errorMsg}`);
    } finally {
      this.downloading = false;
      this._cdr.detectChanges();
    }
  }

  private getFileNameFromUrl(url: string): string {
    try {
      const cleanUrl = url.split('?')[0];
      const segments = cleanUrl.split('/');
      const name = segments[segments.length - 1];
      return name && name.toLowerCase().endsWith('.pdf') ? name : 'document.pdf';
    } catch {
      return 'document.pdf';
    }
  }
}
